import { TellescopeModelFromName } from "../../mappings";
import { ParamsForMethod } from "../../types";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { addMinutes } from "date-fns/addMinutes";
import { endOfDay } from "date-fns/endOfDay";
import { endOfMonth } from "date-fns/endOfMonth";
import { endOfWeek } from "date-fns/endOfWeek";
import { endOfYear } from "date-fns/endOfYear";
import { startOfDay } from "date-fns/startOfDay";
import { startOfMonth } from "date-fns/startOfMonth";
import { startOfWeek } from "date-fns/startOfWeek";
import { startOfYear } from "date-fns/startOfYear";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;
const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;
const PAGE_ITEM_LIMIT = 1000;

export type CalendarViewName = "day" | "week" | "month" | "year" | "list";

const computeBounds = (
  date: Date,
  view: CalendarViewName,
  weekStartsOn: 0 | 1,
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const anchor = timezone ? toZonedTime(date, timezone) : date;
  const toAbs = (d: Date) => (timezone ? fromZonedTime(d, timezone) : d);
  switch (view) {
    case "day":
      return { startDate: toAbs(startOfDay(anchor)), endDate: toAbs(endOfDay(anchor)) };
    case "week":
      return {
        startDate: toAbs(startOfWeek(anchor, { weekStartsOn })),
        endDate: toAbs(endOfWeek(anchor, { weekStartsOn })),
      };
    case "month":
    case "list":
      return { startDate: toAbs(startOfMonth(anchor)), endDate: toAbs(endOfMonth(anchor)) };
    case "year":
      return { startDate: toAbs(startOfYear(anchor)), endDate: toAbs(endOfYear(anchor)) };
  }
};

export interface UseCalendarViewQueryArgs<Count extends boolean = false> {
  referenceDate: Date;
  view: CalendarViewName;
  timezone?: string;
  weekStart?: "sunday" | "monday";
  count?: Count;
  lookbackInMS?: number;
  lookForwardInMS?: number;
  mdbFilter?: ParamsForMethod<"calendar_events", "getSome">["mdbFilter"];
  fetchAllPages?: boolean;
  enabled?: boolean;
}

export const useCalendarViewQuery = <Count extends boolean = false>({
  referenceDate,
  view,
  timezone,
  weekStart,
  count,
  lookbackInMS = TWO_WEEKS_IN_MS,
  lookForwardInMS = TWO_DAYS_IN_MS,
  mdbFilter,
  fetchAllPages = true,
  enabled = true,
}: UseCalendarViewQueryArgs<Count>) => {
  const resolvedCount = count ?? (false as Count);
  const weekStartsOn: 0 | 1 = weekStart === "monday" ? 1 : 0;
  type TransformedData = Count extends true ? number : TellescopeModelFromName<"calendar_events">[];

  const queryBounds = computeBounds(referenceDate, view, weekStartsOn);
  const queryStartMs = queryBounds.startDate.getTime() - lookbackInMS;
  const queryEndMs = queryBounds.endDate.getTime() + lookForwardInMS;

  const displayBounds = computeBounds(referenceDate, view, weekStartsOn, timezone);
  const displayStartMs = displayBounds.startDate.getTime();
  const displayEndMs = displayBounds.endDate.getTime();

  const query = useTellescopeInfiniteQuery({
    model: "calendar_events",
    method: "getSome",
    request: {
      mdbFilter: {
        ...mdbFilter,
        startTimeInMS: {
          ...mdbFilter?.startTimeInMS,
          $gte: queryStartMs,
          $lt: queryEndMs,
        },
      },
      limit: PAGE_ITEM_LIMIT,
    },
    select: (data): TransformedData => {
      const events = data.pages.flat();
      const filteredEvents = events.filter((event) => {
        const eventEnd = addMinutes(
          new Date(event.startTimeInMS),
          event.actualDuration ?? event.durationInMinutes
        ).getTime();
        return event.startTimeInMS < displayEndMs && eventEnd > displayStartMs;
      });

      if (resolvedCount) {
        return filteredEvents.length as TransformedData;
      }

      return filteredEvents as TransformedData;
    },
    getNextPageParam: resolvedCount
      ? (lastPage) => (lastPage.length < PAGE_ITEM_LIMIT ? undefined : lastPage.at(-1)?.id)
      : undefined,
    fetchAllPages,
    placeholderData: (prev) => prev,
    enabled,
  });

  return query;
};
